/**
 * @file AnnotationsTableForm.js
 * @description used to display a table of annotations
 *
 * @copyright veriome labs, llc.  - all rights reserved.
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.md', which is part of this source code package.
 *
 * @author don michael
 * @since Dec 08, 2022 18:10
 */
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import Highlighter from 'react-highlight-words';

import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { FilePenLine } from "lucide-react";

import VisibilityTwoToneIcon from '@mui/icons-material/VisibilityTwoTone';

import { arrayExists } from 'src/@common/utilities';

function findGDots(variantStr) {
    const searchWords = [];

    const strWords = variantStr.match(/[^\s]+|\s+[^\s+]$/g);

    if (arrayExists(strWords)) {
        strWords.forEach((word) => {
            if (word.includes('g.')) {
                searchWords.push(word);
            }
        });
    }

    return searchWords;
}
function AnnotationsTableForm(props) {
    const {
        rows,
        rowsAreSelected,
        editable,
        selectable,
        tableSx,
        viewable,
        handleCheckAllChange,
        handleCheckChange,
        handleEdit,
        handleView,
    } = props;

    const [checkAll, setCheckAll] = useState(false);

    useEffect(() => {
        if (!rowsAreSelected) {
            setCheckAll(false);
        }
    }, [rowsAreSelected]);

    return (

        <TableContainer sx={tableSx}>

            <Table size="small"
                stickyHeader
                sx={{ minWidth: 650 }}
                aria-label="annotations"
            >
                <TableHead>

                    <TableRow>

                        {selectable &&
                            <TableCell >
                                <Checkbox
                                    checked={checkAll}
                                    onChange={(event) => {
                                        setCheckAll(event.target.checked);
                                        handleCheckAllChange(event);
                                    }}
                                    inputProps={{ 'aria-label': 'checkAll' }}
                                />
                            </TableCell>
                        }

                        <TableCell>
                            <Typography component="span" className="font-semibold flex">
                                Annotation Type
                            </Typography>
                        </TableCell>

                        <TableCell>
                            <Typography component="span" className="font-semibold flex">
                                Gene Symbol
                            </Typography>
                        </TableCell>

                        <TableCell>
                            <Typography component="span" className="font-semibold flex">
                                Classification Code
                            </Typography>
                        </TableCell>

                        <TableCell>
                            <Typography component="span" className="font-semibold flex">
                                Curated Text
                            </Typography>
                        </TableCell>

                        <TableCell>
                            <Typography component="span" className="font-semibold flex">
                                Disease
                            </Typography>
                        </TableCell>

                        <TableCell>
                            <Typography component="span" className="font-semibold flex">
                                Variants
                            </Typography>
                        </TableCell>

                        <TableCell>
                            <Typography component="span" className="font-semibold flex">
                                PMID
                            </Typography>
                        </TableCell>

                        <TableCell>
                            <Typography component="span" className="font-semibold flex">
                                Created By
                            </Typography>
                        </TableCell>

                        {selectable === true &&
                            < TableCell align="center">{'Actions'}</TableCell>
                        }

                    </TableRow>

                </TableHead>

                <TableBody>

                    {rows.map((row, rowIndex) => (

                        <TableRow
                            key={rowIndex}
                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        >
                            {selectable &&
                                <TableCell
                                    component="th"
                                    scope="row"
                                >
                                    <Checkbox
                                        checked={row.selected}
                                        onChange={(event) => {
                                            handleCheckChange(event, row._id);
                                        }}
                                        inputProps={{ 'aria-label': 'row checkbox' }}
                                    />
                                </TableCell>
                            }

                            <TableCell >
                                {row.annotationType}
                            </TableCell>

                            <TableCell >
                                {row.geneSymbol}
                            </TableCell>

                            <TableCell >
                                {row.classificationCode}
                            </TableCell>

                            <TableCell >
                                {row.revisedText}
                            </TableCell>

                            <TableCell >
                                {row.disease}
                            </TableCell>

                            <TableCell>
                                <Highlighter
                                    searchWords={findGDots(row.variants)}
                                    autoEscape={true}
                                    textToHighlight={row.variants}
                                />
                            </TableCell>

                            <TableCell >
                                {row.pmId}
                            </TableCell>

                            <TableCell >
                                {row.createdBy}
                            </TableCell>

                            <TableCell align="center" >
                                <Typography noWrap>

                                    {viewable &&
                                        <Tooltip title={'View'} arrow>
                                            <IconButton
                                                onClick={(ev) => {
                                                    ev.preventDefault();
                                                    handleView(row._id);
                                                }}
                                                color="primary"
                                            >
                                                <VisibilityTwoToneIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    }

                                    {editable &&
                                        <Tooltip title={'Edit'} arrow>
                                            <IconButton
                                                onClick={(ev) => {
                                                    ev.preventDefault();
                                                    handleEdit(row._id);
                                                }}
                                                color="primary"
                                            >
                                                <FilePenLine fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    }

                                </Typography>
                            </TableCell>

                        </TableRow>
                    ))}

                </TableBody>

            </Table>

        </TableContainer >

    );
}

AnnotationsTableForm.propTypes = {
    rows: PropTypes.array.isRequired,
    rowsAreSelected: PropTypes.bool,
    editable: PropTypes.bool,
    selectable: PropTypes.bool,
    tableSx: PropTypes.object,
    viewable: PropTypes.bool,
    handleCheckAllChange: PropTypes.func.isRequired,
    handleCheckChange: PropTypes.func.isRequired,
    handleEdit: PropTypes.func.isRequired,
    handleView: PropTypes.func.isRequired,
};

export default AnnotationsTableForm;